import { useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Paperclip, Upload, Trash2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (!b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

// Single supporting PDF attached to a proposal to back up the calculation.
export default function ProposalAttachment({ proposalId, attachment, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => {
    if (!proposalId) return toast.error("Save the proposal draft first, then attach a PDF.");
    fileRef.current?.click();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf") return toast.error("Only PDF files are allowed");
    if (f.size > 25 * 1024 * 1024) return toast.error("PDF must be 25MB or smaller");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/proposals/${proposalId}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
      toast.success("Supporting PDF attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const toggleVisible = async (v) => {
    try { const { data } = await api.patch(`/proposals/${proposalId}/attachment`, { visible_to_client: v }); onChange(data); }
    catch { toast.error("Could not update visibility"); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/proposals/${proposalId}/attachment`); onChange(null); toast.success("Attachment removed"); }
    catch { toast.error("Could not remove attachment"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-md" data-testid="proposal-attachment">
      <div className="flex items-center gap-2 mb-1">
        <Paperclip className="w-4 h-4 text-cass-teal" />
        <h4 className="font-medium text-cass-navy">Supporting document</h4>
      </div>
      <p className="text-xs text-cass-slate mb-3">Attach one PDF to back up the calculation (e.g. prior accounts or a scope). Kept for internal reference — optionally shown to the client on the online proposal and appended to the proposal PDF.</p>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="attachment-file-input" />
      {attachment ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2">
            <a href={attachment.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0" data-testid="attachment-link">
              <FileText className="w-4 h-4 text-cass-navy shrink-0" />
              <span className="text-sm text-cass-navy truncate">{attachment.filename}</span>
              <span className="text-xs text-cass-slate shrink-0">{fmtSize(attachment.size)}</span>
            </a>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="outline" onClick={pick} disabled={busy} data-testid="attachment-replace-btn">Replace</Button>
              <Button size="icon" variant="ghost" onClick={remove} disabled={busy} data-testid="attachment-remove-btn"><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-cass-navy">
            <Switch checked={!!attachment.visible_to_client} onCheckedChange={toggleVisible} data-testid="attachment-visible-toggle" />
            Show this document to the client (online proposal &amp; PDF)
          </label>
        </div>
      ) : (
        <Button variant="outline" onClick={pick} disabled={busy} data-testid="attachment-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload PDF
        </Button>
      )}
    </Card>
  );
}
